import React, { useState } from 'react';
import { Button, RoleGuard, Table, useTable } from 'portal-commons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Container, Grid, withStyles } from '@material-ui/core';

import { getResellerListApi } from '../apiServices';
import {
  ResellerListingRow,
  DeleteResellerModal,
  AddResellerModal,
} from '../components';
import { S3_ASSETS_PATH } from '@/constants';
import Loader from '@/shared_elements/containers/Loader';

import '@/assets/styles/resellers-module.scss';

const headRows = [
  { id: 'companyName', label: 'RESELLER LEGAL NAME', sortable: true },
  { id: 'uid', label: 'RESELLER ID', sortable: true },
  { id: 'email', label: 'E-MAIL ADDRESS', sortable: true },
  { id: 'phone', label: 'PHONE NUMBER', sortable: true },
];

const styles = {
  title: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
    fontFamily: 'Roboto, sans-serif',
    fontWeight: 600,
    fontSize: '27px',
    lineHeight: '32px',
    color: '#19262A',
  },
};

const DEFAULT_FILTER = {
  sortField: 'createDate',
  ascendingOrder: false,
  page: 1,
};

const Resellers = (props) => {
  const { classes, location } = props;
  const [addReseller, setAddReseller] = useState(false);
  const [editReseller, setEditReseller] = useState(false);
  const [deleteReseller, setDeleteReseller] = useState(false);
  const [deleteUid, setDeleteUid] = useState('');
  const [editResellerInfo, setEditResellerInfo] = useState(null);
  const [updateUid, setUpdateUid] = useState('');
  const [noResellersFound, setNoResellerFound] = useState(false);

  const table = useTable(
    async (filter) => {
      const query = {
        ...filter,
        recordsPerPage: 10,
      };
      const response = await getResellerListApi(query);
      setNoResellerFound(response?.totalRecords === 0);

      return {
        data: response?.records || [],
        total: response?.totalRecords || 0,
        page: response?.page || 1,
        rowsPerPage: response?.recordsPerPage || 10,
      };
    },
    {
      deps: [location.search],
      defaultFilter: DEFAULT_FILTER,
    }
  );

  const handleDeleteResellerClick = (flag, resellerUid = '', success = '') => {
    setDeleteReseller(flag);
    setDeleteUid(resellerUid);
    if (success) {
      table.reload();
    }
  };

  const handleAddResellerClick = (flag, success = '') => {
    setAddReseller(flag);
    if (success) {
      table.reload(DEFAULT_FILTER);
    }
  };

  const handleEditResellerClick = (flag, reseller = '', success = '') => {
    if (flag && reseller) {
      const info = {
        companyName: reseller.companyName,
        firstName: reseller.firstName,
        lastName: reseller.lastName,
        email: reseller.email,
        phone: reseller.phone,
      };
      setEditResellerInfo(info);
      setUpdateUid(reseller.uid);
    } else if (success) {
      setEditResellerInfo(null);
      setUpdateUid('');
      table.reload();
    }
    setEditReseller(flag);
  };

  return (
    <>
      <section className="resellers-section" data-testid="resellersSection">
        {table.pageLoading ? (
          <Loader />
        ) : noResellersFound ? (
          <Container maxWidth={false} className="resellers-container">
            <Grid
              container
              className="no-resellers-found"
              alignItems="center"
              justify="center"
            >
              <Grid item className="text-center">
                <div className="outer-wrapper">
                  <img
                    src={`${S3_ASSETS_PATH}/images/no-campaigns-found.svg`}
                    alt=""
                  />
                  <h3 className="heading1">Please register a Reseller</h3>
                  <p className="heading1">No Resellers found</p>
                </div>
                <RoleGuard feature="resellerList.addReseller">
                  <div className="bottom-blk">
                    <a
                      data-testid="resellersSectionRegisterButton"
                      onClick={() => handleAddResellerClick(true)}
                      className="primary-btn"
                    >
                      Register a Reseller
                    </a>
                  </div>
                </RoleGuard>
              </Grid>
            </Grid>
          </Container>
        ) : (
          <Container maxWidth={false} className="resellers-container">
            <div className={classes.title}>{table.total || 0} Resellers</div>
            <RoleGuard feature="resellerList.addReseller">
              <Grid container className="heading list-unstyled">
                <div className="button-wrapper">
                  <Button
                    data-testid="resellersSectionAddResellerButton"
                    onClick={() => handleAddResellerClick(true)}
                    className="primary-btn"
                  >
                    <FontAwesomeIcon icon={faPlus} /> Add new Reseller
                  </Button>
                </div>
              </Grid>
            </RoleGuard>
            <Grid container justify="center" spacing={0}>
              <Table
                headRows={headRows}
                emptyState="no resellers to view"
                className="resellers-listing"
                disableHover
                loading={table.loading}
                tableData={table.tableData.map((record) => (
                  <ResellerListingRow
                    key={record.uid}
                    data={record}
                    handleDelete={handleDeleteResellerClick}
                    handleEdit={handleEditResellerClick}
                  />
                ))}
                handleChangePage={table.handleChangePage}
                createSortHandler={table.handleSorting}
                filter={table.filter}
                pagination={table.pagination}
              />
            </Grid>
          </Container>
        )}
        {deleteReseller && (
          <DeleteResellerModal
            deleteId={deleteUid}
            handleModalClick={handleDeleteResellerClick}
          />
        )}
        {(addReseller || editReseller) && (
          <AddResellerModal
            open={addReseller || editReseller}
            handleModalClick={
              addReseller ? handleAddResellerClick : handleEditResellerClick
            }
            modalType={editReseller ? 'edit' : 'add'}
            reseller={editReseller ? editResellerInfo : null}
            updateId={editReseller ? updateUid : ''}
          />
        )}
      </section>
    </>
  );
};

export default withStyles(styles)(Resellers);
